import { Avatar, Box } from '@mui/joy';
import { ICellRendererParams } from 'ag-grid-community';
import { ReactNode } from 'react';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { AgGridDataRow } from 'core/types';
import { getAgGridTableCellData } from 'core/utils';
import { Color, Size, Variant } from 'theme/enums';

import { TextEllipsis } from '.';

interface IProps extends ICellRendererParams {
  colId?: string;
  openDetails?: (data: AgGridDataRow) => void;
  displayEmpty?: (data: AgGridDataRow) => ReactNode;
}

export const AvatarNameCell = ({
  colId = 'name',
  openDetails,
  displayEmpty = defaultDisplayEmpty,
  ...props
}: IProps) => {
  const data = getAgGridTableCellData(props, colId);

  const { value } = props;

  if (!data) {
    return value || null;
  }

  if (!value) {
    return displayEmpty(data);
  }

  return (
    <Box display="flex" alignItems="center" gap={1} onClick={() => openDetails?.(data)}>
      <Avatar
        size={Size.SM}
        variant={Variant.SOLID}
        sx={{ bgcolor: getColor(value), cursor: openDetails ? 'pointer' : 'default' }}
      >
        {getAvatarInitials(value)}
      </Avatar>

      <TextEllipsis
        content={value}
        color={openDetails ? Color.PRIMARY : null}
        sx={{
          cursor: openDetails ? 'pointer' : 'default',

          '&:hover': {
            textDecoration: openDetails ? 'underline' : 'none',
          },
        }}
      />
    </Box>
  );
};

const DEFAULT_COLORS = [
  '#555E68',
  '#506A86',
  '#796397',
  '#394B5F',
  '#3B584E',
  '#686255',
  '#685560',
  '#685555',
  '#4D6074',
  '#593752',
  '#55685E',
  '#63657F',
  '#677F79',
  '#6A6C8A',
  '#4C525E',
  '#68555D',
  '#685560',
  '#55685D',
  '#5A5568',
  '#697089',
  '#435D4D',
  '#685568',
  '#586855',
  '#292844',
];

const getColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return DEFAULT_COLORS[Math.abs(hash) % DEFAULT_COLORS.length];
};

const defaultDisplayEmpty = () => DEFAULT_PLACEHOLDER;

const getAvatarInitials = (name: string): string => {
  if (!name || typeof name !== 'string') {
    return '??';
  }

  const cleanName = name
    .replace(/\[.*?\]/g, '')
    .replace(/\(.*?\)/g, '')
    .replace(/\{.*?\}/g, '')
    .trim();

  const words = cleanName.split(/\s+/).filter((word) => word.length > 0);

  const initials = words
    .map((word) => {
      const firstLetter = word.match(/[a-zA-Z]/);

      return firstLetter ? firstLetter[0].toUpperCase() : null;
    })
    .filter(Boolean)
    .slice(0, 2);

  // If no letters found, try to get first 2 characters that aren't special
  if (initials.length === 0) {
    const fallback = cleanName
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 2)
      .toUpperCase();

    return fallback || '??';
  }

  // If only one word, try to get second letter from the same word
  if (initials.length === 1) {
    const firstWord = words[0];
    const secondLetter = firstWord.slice(1).match(/[a-zA-Z]/);
    if (secondLetter) {
      initials.push(secondLetter[0].toUpperCase());
    }
  }

  return initials.join('').slice(0, 2);
};
